import React, { useMemo, useState } from 'react';
import COUNTRIES from '../data/countries.json';
import { LANGUAGES, formatLanguages, parseLanguages } from '../lib/languages';

/* Country, timezone and languages — picked, not free-typed.

   Location is a country, not an address: it is a filter, and a city adds
   nothing matching uses. Picking a country fills the timezone when the country
   has only one; for the US, Australia, Brazil and the like you choose it. */

const MAX_SUGGESTIONS = 8;

/* Every offset in use somewhere, so the list never lacks the one you need. */
export const OFFSETS = [
  'GMT-12', 'GMT-11', 'GMT-10', 'GMT-9:30', 'GMT-9', 'GMT-8', 'GMT-7', 'GMT-6', 'GMT-5',
  'GMT-4', 'GMT-3:30', 'GMT-3', 'GMT-2', 'GMT-1', 'GMT+0', 'GMT+1', 'GMT+2', 'GMT+3',
  'GMT+3:30', 'GMT+4', 'GMT+4:30', 'GMT+5', 'GMT+5:30', 'GMT+5:45', 'GMT+6', 'GMT+6:30',
  'GMT+7', 'GMT+8', 'GMT+8:45', 'GMT+9', 'GMT+9:30', 'GMT+10', 'GMT+10:30', 'GMT+11',
  'GMT+12', 'GMT+12:45', 'GMT+13', 'GMT+14',
];

/* "GMT+2", "GMT-5", "GMT+5:30" for an IANA zone, at today's offset. */
export function tzLabel(zone) {
  try {
    const part = new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'shortOffset' })
      .formatToParts(new Date())
      .find((p) => p.type === 'timeZoneName');
    const value = part?.value || '';
    return value === 'GMT' ? 'GMT+0' : value;
  } catch {
    return '';
  }
}

/* This device's current offset. */
export function deviceTimezone() {
  try {
    return tzLabel(Intl.DateTimeFormat().resolvedOptions().timeZone);
  } catch {
    return '';
  }
}

/* 🇸🇪 from "SE": each letter maps to its regional-indicator symbol. */
export function flag(code) {
  return code.toUpperCase().replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));
}

const names = (() => {
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' });
  } catch {
    return null;
  }
})();

/* Every country with its English name and the distinct offsets it uses today. */
const LIST = COUNTRIES
  .map((c) => ({
    code: c.code,
    name: names?.of(c.code) || c.code,
    offsets: [...new Set(c.zones.map(tzLabel).filter(Boolean))]
      .sort((a, b) => OFFSETS.indexOf(a) - OFFSETS.indexOf(b)),
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

/* The country a stored location refers to — "Sweden", or an older
   "Stockholm, Sweden" — or null for anything else. */
export function countryFor(location) {
  const value = (location || '').trim().toLowerCase();
  if (!value) return null;
  const last = value.split(',').pop().trim();
  return LIST.find((c) => c.name.toLowerCase() === value || c.name.toLowerCase() === last) || null;
}

/* The profile change for choosing a country: its name, plus the timezone when
   there is only one it could be. */
export function countryPatch(country, currentTimezone) {
  const patch = { location: country.name };
  if (country.offsets.length === 1) patch.timezone_label = country.offsets[0];
  else if (currentTimezone && !country.offsets.includes(currentTimezone)) patch.timezone_label = '';
  return patch;
}

/* The device's country is not knowable without asking for location; its
   timezone is. So "this device" means the timezone only. */

function searchCountries(query) {
  const q = query.trim().toLowerCase();
  if (!q) return LIST.slice(0, MAX_SUGGESTIONS);
  const starts = LIST.filter((c) => c.name.toLowerCase().startsWith(q) || c.code.toLowerCase() === q);
  const contains = LIST.filter((c) => !starts.includes(c) && c.name.toLowerCase().includes(q));
  return [...starts, ...contains].slice(0, MAX_SUGGESTIONS);
}

function searchLanguages(query, taken) {
  const q = query.trim().toLowerCase();
  const free = LANGUAGES.filter((l) => !taken.includes(l));
  if (!q) return [];
  const starts = free.filter((l) => l.toLowerCase().startsWith(q));
  const contains = free.filter((l) => !l.toLowerCase().startsWith(q) && l.toLowerCase().includes(q));
  return [...starts, ...contains].slice(0, MAX_SUGGESTIONS);
}

/* The dropdown both pickers share. Options are chosen on mousedown so the
   input's blur (which saves) cannot fire first and close the list. */
function Suggestions({ id, items, active, render, onPick }) {
  if (items.length === 0) return null;
  return (
    <ul className="suggest" role="listbox" id={id}>
      {items.map((item, i) => (
        <li
          key={render(item)}
          role="option"
          aria-selected={i === active}
          className={i === active ? 'on' : ''}
          onMouseDown={(e) => { e.preventDefault(); onPick(item); }}
        >
          {render(item)}
        </li>
      ))}
    </ul>
  );
}

function useListKeys(items, onPick, onClose) {
  const [active, setActive] = useState(0);
  const onKeyDown = (e) => {
    if (items.length === 0) return false;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => (a + 1) % items.length); return true; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => (a - 1 + items.length) % items.length); return true; }
    if (e.key === 'Enter') { e.preventDefault(); onPick(items[Math.min(active, items.length - 1)]); return true; }
    if (e.key === 'Escape') { onClose(); return true; }
    return false;
  };
  return { active, setActive, onKeyDown };
}

/* A searchable country list with flags. `onPick(country)` receives the chosen
   country; typing alone never saves, so the field only ever holds a real
   country (or whatever an older profile already had). */
export function CountryInput({ id, value, onPick, placeholder }) {
  const current = countryFor(value);
  const [query, setQuery] = useState(null); // null = not searching
  const [open, setOpen] = useState(false);
  const items = useMemo(() => (open ? searchCountries(query || '') : []), [open, query]);
  const pick = (country) => {
    setOpen(false);
    setQuery(null);
    onPick(country);
  };
  const keys = useListKeys(items, pick, () => { setOpen(false); setQuery(null); });
  const shown = query !== null ? query : (current ? `${flag(current.code)}  ${current.name}` : (value || ''));

  return (
    <div className="suggest-wrap">
      <input
        id={id}
        className="in"
        value={shown}
        placeholder={placeholder}
        autoComplete="off"
        role="combobox"
        aria-expanded={items.length > 0}
        aria-controls={`${id}-list`}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); keys.setActive(0); }}
        onFocus={(e) => { setOpen(true); e.target.select(); }}
        onBlur={() => { setOpen(false); setQuery(null); }}
        onKeyDown={keys.onKeyDown}
      />
      <Suggestions
        id={`${id}-list`}
        items={items}
        active={keys.active}
        render={(c) => `${flag(c.code)}  ${c.name}`}
        onPick={pick}
      />
    </div>
  );
}

/* GMT offsets, with this device's offset one click away. When a country is
   known, its own offsets are listed first. */
export function TimezoneSelect({ id, value, country, onChange }) {
  const device = deviceTimezone();
  const mine = country?.offsets || [];
  const rest = OFFSETS.filter((o) => !mine.includes(o));
  const unknown = value && !OFFSETS.includes(value) && !mine.includes(value);

  return (
    <>
      <select id={id} className="in" value={value || ''} onChange={(e) => onChange(e.target.value)}>
        <option value="">Choose a timezone</option>
        {unknown && <option value={value}>{value}</option>}
        {mine.length > 1 && (
          <optgroup label={`In ${country.name}`}>
            {mine.map((o) => <option key={`c-${o}`} value={o}>{o}</option>)}
          </optgroup>
        )}
        <optgroup label={mine.length > 1 ? 'All' : 'Timezones'}>
          {(mine.length > 1 ? rest : OFFSETS).map((o) => <option key={o} value={o}>{o}</option>)}
        </optgroup>
      </select>
      {device && value !== device && (
        <button className="linklike tz-device" type="button" onClick={() => onChange(device)}>
          Use {device} (from this device)
        </button>
      )}
    </>
  );
}

/* Chips, alphabetical, with a search box. Stored as one comma-separated string. */
export function LanguagePicker({ id, value, onChange }) {
  const chosen = parseLanguages(value).sort((a, b) => a.localeCompare(b));
  const [query, setQuery] = useState('');
  const items = searchLanguages(query, chosen);

  const add = (lang) => {
    const clean = lang.trim();
    if (!clean) return;
    const proper = LANGUAGES.find((l) => l.toLowerCase() === clean.toLowerCase())
      || clean.charAt(0).toUpperCase() + clean.slice(1);
    if (!chosen.includes(proper)) onChange(formatLanguages([...chosen, proper]));
    setQuery('');
  };
  const remove = (lang) => onChange(formatLanguages(chosen.filter((l) => l !== lang)));
  const keys = useListKeys(items, add, () => setQuery(''));

  return (
    <div className="suggest-wrap">
      <div className="lang-box">
        {chosen.map((lang) => (
          <span className="lang-chip" key={lang}>
            {lang}
            <button type="button" aria-label={`Remove ${lang}`} onClick={() => remove(lang)}>×</button>
          </span>
        ))}
        <input
          id={id}
          className="lang-input"
          value={query}
          placeholder={chosen.length ? 'Add another…' : 'Search languages…'}
          autoComplete="off"
          role="combobox"
          aria-expanded={items.length > 0}
          aria-controls={`${id}-list`}
          onChange={(e) => { setQuery(e.target.value); keys.setActive(0); }}
          onKeyDown={(e) => {
            if (keys.onKeyDown(e)) return;
            // Not on the list: Enter or a comma keeps what was typed.
            if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(query); }
            if (e.key === 'Backspace' && !query && chosen.length) remove(chosen[chosen.length - 1]);
          }}
          onBlur={() => { if (query.trim()) add(query); }}
        />
      </div>
      <Suggestions id={`${id}-list`} items={items} active={keys.active} render={(l) => l} onPick={add} />
    </div>
  );
}
